import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Card, Radio, Typography, message } from 'antd';
import type { AttendanceMode } from '@velora/contracts';
import { StepFooter } from '../StepFooter';
import { ApiError, productApi } from '../../../api';

/**
 * Setup step 5 — Attendance mode. Chosen once but changeable (the shape is the
 * same either way). Reads and writes institution.attendance_mode. The InstCtx
 * doesn't yet expose this — we fetch progress to derive "already saved".
 */
export function AttendanceModePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();

  const progress = useQuery({
    queryKey: ['setup-progress', institution],
    queryFn: () => productApi.getSetupProgress(institution),
    enabled: !!institution,
  });

  const savedEntry = progress.data?.entries.find((e) => e.step === 'attendance_mode');
  const savedMode: AttendanceMode | null =
    savedEntry?.summary === 'daily' || savedEntry?.summary === 'period' ? savedEntry.summary : null;

  const [mode, setMode] = useState<AttendanceMode>('daily');

  // Once — hydrate the local choice from the persisted value.
  useEffect(() => {
    if (savedMode) setMode(savedMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedMode]);

  const save = useMutation({
    mutationFn: (m: AttendanceMode) => productApi.setAttendanceMode(institution, { mode: m }),
    onSuccess: async (_v, chosenMode) => {
      // Also flip the setup_progress row with the mode as summary so the next
      // page render knows what was picked without fetching the institution row.
      await productApi.markStepDone(institution, { step: 'attendance_mode', summary: chosenMode });
      void qc.invalidateQueries({ queryKey: ['setup-progress', institution] });
      void qc.invalidateQueries({ queryKey: ['institution', institution] });
      void message.success('Attendance mode saved');
    },
  });

  const body = save.error instanceof ApiError ? (save.error.body as { message?: string } | null) : null;
  const err = body?.message ?? (save.error instanceof Error ? save.error.message : null);

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          How attendance works here
        </Typography.Title>
        <Typography.Text type="secondary">
          Can differ by grade later. The shape is the same either way — one register per day, one
          row per student.
        </Typography.Text>
      </div>

      <Card size="small">
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value as AttendanceMode)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Radio value="daily">
            <div style={{ display: 'inline-block', marginLeft: 6 }}>
              <div style={{ fontWeight: 600 }}>Daily</div>
              <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
                One entry per student per day. Simplest — fits most K–12 schools.
              </Typography.Text>
            </div>
          </Radio>
          <Radio value="period">
            <div style={{ display: 'inline-block', marginLeft: 6 }}>
              <div style={{ fontWeight: 600 }}>Period-wise</div>
              <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
                One entry per student per period. Fits colleges and schools that need bunk tracking.
              </Typography.Text>
            </div>
          </Radio>
        </Radio.Group>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => save.mutate(mode)}
            disabled={save.isPending}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: '#2f6ab0',
              color: '#fff',
              cursor: save.isPending ? 'wait' : 'pointer',
              opacity: save.isPending ? 0.7 : 1,
            }}
          >
            {savedMode ? 'Update' : 'Save'}
          </button>
          {savedMode && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Saved as <b>{savedMode}</b>
            </Typography.Text>
          )}
        </div>
      </Card>

      {err && <Alert type="error" showIcon style={{ marginTop: 12 }} message={err} />}

      <StepFooter
        institution={institution}
        step="attendance_mode"
        summary={mode}
        disabled={!savedMode}
        disabledReason="Save your choice first"
        nextHref={`/i/${institution}/setup/invite-guardians`}
        nextLabel="Next: invite guardians →"
      />
    </div>
  );
}
